import React, { createContext, useCallback, useContext } from 'react';

import invariant from 'tiny-invariant';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { EVENT_CHANNEL } from '../../../common/analytics/constants';

import type {
	CollapseCallback,
	ExpandCallback,
	ResizeEndCallback,
	ResizeStartCallback,
	SideNavVisibilityContextType,
	SideNavVisibilityProviderProps,
} from './types';

export const SideNavVisibilityContext = createContext<SideNavVisibilityContextType | null>(null);

/*
 * useSideNavVisibility - Returns Visibility callbacks from Navigation System
 */
export const useSideNavVisibility = () => {
	const context = useContext(SideNavVisibilityContext);
	invariant(context, 'useSideNavVisibility must be used within an SideNavVisibilityProvider');

	return context;
};

/*
 *  SideNavVisibilityProvider - Wrapper for the Navigation System visibility callbacks.
 */
export function SideNavVisibilityProvider({
	children,
	onCollapse,
	onExpand,
	onResizeEnd,
	onResizeStart,
	setUserSideNavData,
}: SideNavVisibilityProviderProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onCollapseHandler = useCallback<CollapseCallback>(
		(args) => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'collapsed',
				actionSubject: 'sideNavigation',
				attributes: {},
			}).fire(EVENT_CHANNEL);

			setUserSideNavData({ defaultCollapsed: true });
			onCollapse?.(args);
		},
		[createAnalyticsEvent, onCollapse, setUserSideNavData],
	);

	const onExpandHandler = useCallback<ExpandCallback>(
		(args) => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'expanded',
				actionSubject: 'sideNavigation',
				attributes: {},
			}).fire(EVENT_CHANNEL);

			setUserSideNavData({ defaultCollapsed: false });
			onExpand?.(args);
		},
		[createAnalyticsEvent, onExpand, setUserSideNavData],
	);

	const onResizeStartHandler = useCallback<ResizeStartCallback>(
		(args) => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'resizeStart',
				actionSubject: 'sideNavigation',
				attributes: {},
			}).fire(EVENT_CHANNEL);

			onResizeStart?.(args);
		},
		[onResizeStart, createAnalyticsEvent],
	);

	const onResizeEndHandler = useCallback<ResizeEndCallback>(
		(args) => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'resizeEnd',
				actionSubject: 'sideNavigation',
				attributes: {},
			}).fire(EVENT_CHANNEL);

			setUserSideNavData({ defaultWidth: args.finalWidth });
			onResizeEnd?.(args);
		},
		[createAnalyticsEvent, onResizeEnd, setUserSideNavData],
	);

	return (
		<SideNavVisibilityContext.Provider
			value={{
				onExpand: onExpandHandler,
				onCollapse: onCollapseHandler,
				onResizeEnd: onResizeEndHandler,
				onResizeStart: onResizeStartHandler,
			}}
		>
			{children}
		</SideNavVisibilityContext.Provider>
	);
}
